import { CSSProperties, useState } from "react";
import { CurrentBrand, BrandPermission, MemberRole, OrganizationRoles } from "modules/Types";
import Avatar from '@mui/material/Avatar';
import useScreen from "Hooks/useScreen";
import gravatar from "gravatar";
import BrandsList from "./BrandsList/BrandsList";
import { updateTeamMemberPermissions_api } from "api/organizationApi"
import { toast } from "react-toastify"
import { TOAST_ERROR_OPTIONS, TOAST_SUCCESS_OPTIONS } from "../Organization_constants"
import MemberPermissionsSelect from "./MemberPermissionsSelect";
import useOrganization from 'Hooks/useOrganization';
import ButtonSymphony from "elements/ButtonSymphony";
import ModalSymphony from "elements/ModalSymphony";
import { MEMBER_ROLES_NAMES, USER_PERMISSION_ON_BRAND } from "modules/Const";
import { clsx } from "modules/Utils";
import Colors from "modules/Colors";
import DebugBox from "components/shareable/DebugBox";
import { toastError } from "modules/Toast";

interface EditMemberModalProps {
  className: string;
  isOpen: boolean;
  onClose: () => void;
  onRemoveMember: () => void;
}

// NOTE: TeamMemberList.tsx
const EditMemberModal = ({
  className,
  isOpen,
  onClose,
  onRemoveMember,
}: EditMemberModalProps) => {
  const { mobileView } = useScreen();
  const {
    getSelectedTeamMember,
    getOrganizationTeamMembers,
    isLoadingTeamMembers,
    setIsLoadingTeamMembers,
    isSelectedTeamMemberMyself,
    getMyselfAsTeamMember,
    getOrganizationId
  } = useOrganization();

  const selectedTeamMember = getSelectedTeamMember();

  const {
    name = '',
    lastName = '',
    email = '',
  } = selectedTeamMember || {}

  const [teamMemberBrands, setTeamMemberBrands] = useState<CurrentBrand[]>(selectedTeamMember?.brands || [])
  const [tempTeamMemberRole, setTempTeamMemberRole] = useState(selectedTeamMember?.role || '');

  const disableSaveButton = () => Boolean((teamMemberBrands || []).find(artist => !artist.permissionLevel))

  /**
     * Determines if the current member can edit another member's permissions
     * 
     * Edit Rules:
     * 1. Owners (priority 0):
     *    - Can edit all other roles (Admin priority 1, Member priority 2)
     *    - Can edit themselves
     * 
     * 2. Admins (priority 1):
     *    - Can edit Members (priority 2)
     *    - Can edit themselves
     *    - Cannot edit Owners or other Admins
     * 
     * 3. Members (priority 2):
     *    - Cannot edit anyone
     * 
     * @returns {boolean} Whether the current member can edit the target member
     */
  const canEditMember = (): boolean => {
    if (!selectedTeamMember) {
      return false
    }

    const myselfAsTeamMember = getMyselfAsTeamMember()!;
    const myPriority = MEMBER_ROLES_NAMES[myselfAsTeamMember.role].priority;
    const theirPriority = MEMBER_ROLES_NAMES[selectedTeamMember.role].priority;

    // Special case: Owners and Admins can edit themselves
    if (isSelectedTeamMemberMyself()) {
      return selectedTeamMember.role === OrganizationRoles.ADMIN || selectedTeamMember.role === OrganizationRoles.OWNER;
    }

    // For editing other members: can only edit if current member's priority is LOWER (numerically)
    // than the target member's priority (e.g., Owner(0) can edit Admin(1))
    return myPriority < theirPriority;
  }

  const getFormattedChanges = (): {
    artistsToAdd: { brandId: number; permission: BrandPermission; }[];
    artistsToEdit: { brandId: number; permission: "view_only" | "administrator"; }[];
    artistsToDelete: number[];
    roleChanged: string | undefined;
  } | undefined => {

    if (!selectedTeamMember) {
      return undefined;
    }
    const artistsToAdd = teamMemberBrands.filter(brand => !selectedTeamMember.brands.find(a => a.id === brand.id))
    const artistsToAddFormatted = artistsToAdd.map(artist => ({ brandId: artist.id, permission: artist?.permissionLevel || 'view_only' }))

    const artistsToDelete = selectedTeamMember.brands.filter(brand => !teamMemberBrands.find(a => a.id === brand.id))
    const artistsToDeleteFormatted = artistsToDelete.map(brand => brand.id)

    const artistsToEdit = selectedTeamMember.brands.filter(brand => teamMemberBrands.find(a => a.id === brand.id && a.permissionLevel !== brand.permissionLevel))
    const artistsToEditFormatted = artistsToEdit.map(brand => ({
      brandId: brand.id,
      permission:
        brand?.permissionLevel === USER_PERMISSION_ON_BRAND.VIEW_ONLY
          ? USER_PERMISSION_ON_BRAND.ADMINISTRATOR
          : USER_PERMISSION_ON_BRAND.VIEW_ONLY
    }))

    const changes = {
      artistsToAdd: artistsToAddFormatted,
      artistsToEdit: artistsToEditFormatted,
      artistsToDelete: artistsToDeleteFormatted,
      roleChanged: tempTeamMemberRole !== selectedTeamMember.role ? tempTeamMemberRole : undefined
    }

    return changes;
  }

  const onEditMember = async () => {
    if (!selectedTeamMember) {
      return
    }

    try {
      setIsLoadingTeamMembers(true)
      const formattedChanges = getFormattedChanges()
      if (!formattedChanges) {
        return;
      }
      const { artistsToAdd, artistsToEdit, artistsToDelete, roleChanged } = formattedChanges

      const data = {
        addedBrands: artistsToAdd,
        editedBrands: artistsToEdit,
        removedBrands: artistsToDelete,
        role: roleChanged as MemberRole
      }

      await updateTeamMemberPermissions_api(
        getOrganizationId(),
        selectedTeamMember?.id,
        data
      )
      await getOrganizationTeamMembers()
      toast.success("Member edited", TOAST_SUCCESS_OPTIONS)
      onClose()
    } catch (error) {
      toastError("Something went wrong editing the member")
      console.error(error)
    } finally {
      setIsLoadingTeamMembers(false)
    }
  }

  const renderActionButtons = () => {
    return (
      <div className="buttonActionRow" style={sty.buttonActionRow}>
        {canEditMember() &&
          <ButtonSymphony
            className="buttonRemoveMemberEditMemberModal"
            onClick={onRemoveMember}
            color="red"
            variant="outlined"
          >
            Remove Member
          </ButtonSymphony>
        }
        {canEditMember() ?
          <ButtonSymphony
            className="buttonSaveChangesEditMemberModal"
            isLoading={isLoadingTeamMembers}
            onClick={onEditMember}
            disabled={isLoadingTeamMembers || disableSaveButton()}
            tooltip={disableSaveButton() && "Please select all artist's permissions"}
          >
            Save changes
          </ButtonSymphony>
          :
          <ButtonSymphony
            className="buttonCloseEditMemberModal"
            style={{ marginTop: 16 }}
            onClick={onClose}
          >
            Close
          </ButtonSymphony>
        }
      </div>
    )
  }

  const renderPermissionLevelSelect = () => {
    if (selectedTeamMember?.role === OrganizationRoles.OWNER) {
      return null;
    }

    return (
      <div className="permissionLevelSelect" style={{ marginBottom: 16, width: '100%' }}>
        <div className="permissionLevelSelectTitle" style={{ marginBottom: 8, color: Colors.greyDark }}>
          Permission Level
        </div>
        <MemberPermissionsSelect
          value={tempTeamMemberRole as MemberRole}
          onChange={setTempTeamMemberRole}
          disabled={!canEditMember()}
        />
      </div>
    )
  }

  if (!selectedTeamMember) {
    return null;
  }

  return (
    <ModalSymphony
      className={clsx('editMemberModalTeam', className)}
      style={{ gap: 16 }}
      isOpen={isOpen}
      onClose={onClose}
      title={name ? `${name} ${lastName}` : email}
      subtitle="Edit permissions and access to brands."
      headerIcon={<Avatar src={gravatar.url(email, { protocol: 'https', d: 'retro' })} sx={{ width: 32, height: 32 }} />}
      fullScreen={mobileView}
    >
      {renderPermissionLevelSelect()}
      <BrandsList
        isCurrentUser={isSelectedTeamMemberMyself()}
        brands={teamMemberBrands}
        onUpdateBrands={setTeamMemberBrands}
        canEdit={canEditMember()}
      />
      {renderActionButtons()}
    </ModalSymphony>
  );
}

const sty: Record<string, CSSProperties> = {
  buttonActionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    marginTop: 16,
  },
}

export default EditMemberModal;